// ── Company Backup & Restore ────────────────────────────────────────────────
// A backup is one JSON file holding every document of the company's own
// database, each under its full path (so nested ones, e.g. a sale's
// payments or a Code's history, land back under the same parent with the
// same id). Restore puts the database back to exactly that state: every
// document in the file is written back as it was, and every document that
// isn't in the file (added after the backup) is removed. Running a restore
// twice gives the same result, so a restore cut short is finished by simply
// running it again with the same file.
//
// Firestore can't list a database's collections from the browser, so the
// lists below are the app's own. A NEW COLLECTION MUST BE ADDED HERE, or it
// is left out of backups (and left untouched by a restore).

import {
  collection, collectionGroup, doc, getDocs, writeBatch, setDoc, deleteDoc, onSnapshot,
  Timestamp, GeoPoint, Bytes, DocumentReference, type Firestore,
} from 'firebase/firestore';

/** Every top-level collection in a company's database. */
export const BACKUP_COLLECTIONS = [
  'settings', 'counters', 'entities', 'inventory', 'itemCodes', 'skus',
  'invoices', 'sales', 'purchaseReturns', 'salesReturns',
  'purchaseExpenses', 'saleExpenses', 'expenses', 'recurringExpenses',
  'supplierMemos', 'customerMemos', 'supplierAdvances', 'customerAdvances',
  'bankAccounts', 'bankTransactions', 'bankStatements',
  'ledgerAccounts', 'journalEntries',
  'stockAdjustments', 'stockTransfers', 'stockRevaluations', 'openingStock',
  'reminders', 'tasks', 'activityLog', 'demands', 'demandActivities',
] as const;

/** Nested collections, read wherever they sit (payments under invoices and sales, etc.). */
export const BACKUP_SUBCOLLECTIONS = [
  'payments', 'brokerPayments', 'adjustments', 'applications', 'memos', 'history',
] as const;

const FORMAT  = 'diawolt-backup';
const VERSION = 1;
/** Firestore allows 500 writes per batch; stay under it. */
const BATCH_SIZE = 400;

export interface BackupDoc { path: string; data: Record<string, unknown> }

export interface BackupFile {
  format:       typeof FORMAT;
  version:      number;
  businessId:   string;
  businessName: string;
  createdAt:    string;   // ISO
  createdBy:    string;   // user name
  /** Documents per collection name, for the summary shown before a restore. */
  counts:       Record<string, number>;
  docs:         BackupDoc[];
}

export type Progress = (p: { step: string; done: number; total: number }) => void;

// ── Firestore values ⇄ plain JSON ──────────────────────────────────────────
// Dates written by serverTimestamp() come back as Timestamps, which JSON
// can't hold; they're stored as a tagged object and turned back on restore.

function encode(v: unknown): unknown {
  if (v instanceof Timestamp)          return { __fs: 'ts', s: v.seconds, n: v.nanoseconds };
  if (v instanceof GeoPoint)           return { __fs: 'geo', lat: v.latitude, lng: v.longitude };
  if (v instanceof Bytes)              return { __fs: 'bytes', b64: v.toBase64() };
  if (v instanceof DocumentReference)  return { __fs: 'ref', path: v.path };
  if (Array.isArray(v))                return v.map(encode);
  if (v && typeof v === 'object') {
    const o: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) o[k] = encode(x);
    return o;
  }
  return v;
}

function decode(v: unknown, db: Firestore): unknown {
  if (Array.isArray(v)) return v.map(x => decode(x, db));
  if (v && typeof v === 'object') {
    const t = v as Record<string, unknown>;
    switch (t.__fs) {
      case 'ts':    return new Timestamp(t.s as number, t.n as number);
      case 'geo':   return new GeoPoint(t.lat as number, t.lng as number);
      case 'bytes': return Bytes.fromBase64String(t.b64 as string);
      case 'ref':   return doc(db, t.path as string);
    }
    const o: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(t)) o[k] = decode(x, db);
    return o;
  }
  return v;
}

// ── Reading ────────────────────────────────────────────────────────────────

/** Every document in the company database, as stored (not yet encoded). */
async function readAll(db: Firestore, onProgress?: Progress): Promise<{ path: string; data: Record<string, unknown> }[]> {
  const out: { path: string; data: Record<string, unknown> }[] = [];
  const groups = [
    ...BACKUP_COLLECTIONS.map(c => () => getDocs(collection(db, c))),
    ...BACKUP_SUBCOLLECTIONS.map(c => () => getDocs(collectionGroup(db, c))),
  ];
  let done = 0;
  for (const read of groups) {
    onProgress?.({ step: 'read', done, total: groups.length });
    const snap = await read();
    snap.docs.forEach(d => out.push({ path: d.ref.path, data: d.data() }));
    done++;
  }
  onProgress?.({ step: 'read', done, total: groups.length });
  return out;
}

const collectionOf = (path: string) => { const p = path.split('/'); return p[p.length - 2]; };

export async function createBackup(db: Firestore, meta: { businessId: string; businessName: string; userName: string }, onProgress?: Progress): Promise<BackupFile> {
  const raw = await readAll(db, onProgress);
  const counts: Record<string, number> = {};
  for (const r of raw) { const c = collectionOf(r.path); counts[c] = (counts[c] ?? 0) + 1; }
  return {
    format: FORMAT, version: VERSION,
    businessId: meta.businessId, businessName: meta.businessName,
    createdAt: new Date().toISOString(), createdBy: meta.userName,
    counts,
    docs: raw.map(r => ({ path: r.path, data: encode(r.data) as Record<string, unknown> })),
  };
}

/** Saves the backup as a .json file in the browser's downloads. */
export function downloadBackup(file: BackupFile) {
  const d = new Date(file.createdAt);
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  const slug = file.businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'company';
  const blob = new Blob([JSON.stringify(file)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `DiaWolt-backup-${slug}-${stamp}.json`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Reads and checks a backup file. Throws a plain message when it can't be used. */
export async function readBackupFile(f: File, businessId: string): Promise<BackupFile> {
  let parsed: unknown;
  try { parsed = JSON.parse(await f.text()); }
  catch { throw new Error('This is not a DiaWolt backup file.'); }
  const b = parsed as Partial<BackupFile>;
  if (b?.format !== FORMAT || !Array.isArray(b.docs)) throw new Error('This is not a DiaWolt backup file.');
  if ((b.version ?? 0) > VERSION) throw new Error('This backup was made by a newer version of DiaWolt. Reload the page and try again.');
  if (b.businessId !== businessId) throw new Error(`This backup belongs to another company (${b.businessName ?? 'unknown'}). It can only be restored there.`);
  const bad = b.docs.find(d => typeof d?.path !== 'string' || d.path.split('/').length % 2 !== 0 || !d.data || typeof d.data !== 'object');
  if (bad) throw new Error('This backup file is damaged and can\'t be restored.');
  return b as BackupFile;
}

// ── Restore ────────────────────────────────────────────────────────────────

/**
 * Puts the database back to the backup. `current` is what's there now (the
 * safety backup just taken), so nothing is read twice. Deletes what the
 * backup doesn't have, then writes every backup document as it was.
 */
export async function restoreBackup(db: Firestore, file: BackupFile, current: BackupFile, onProgress?: Progress): Promise<{ removed: number; written: number }> {
  const keep = new Set(file.docs.map(d => d.path));
  const toDelete = current.docs.map(d => d.path).filter(p => !keep.has(p));

  type Op = { kind: 'del'; path: string } | { kind: 'set'; path: string; data: Record<string, unknown> };
  const ops: Op[] = [
    ...toDelete.map(path => ({ kind: 'del' as const, path })),
    ...file.docs.map(d => ({ kind: 'set' as const, path: d.path, data: decode(d.data, db) as Record<string, unknown> })),
  ];

  for (let i = 0; i < ops.length; i += BATCH_SIZE) {
    onProgress?.({ step: 'write', done: i, total: ops.length });
    const batch = writeBatch(db);
    for (const op of ops.slice(i, i + BATCH_SIZE)) {
      if (op.kind === 'del') batch.delete(doc(db, op.path));
      else batch.set(doc(db, op.path), op.data);
    }
    await batch.commit();
  }
  onProgress?.({ step: 'write', done: ops.length, total: ops.length });
  return { removed: toDelete.length, written: file.docs.length };
}

// ── Lock while restoring + last backup note ────────────────────────────────
// Kept in `maintenance/*`, which a backup never contains and a restore never
// touches, so the lock and the note survive the restore itself.

export interface RestoreLock { active: boolean; byId: string; byName: string; startedAt: string; backupDate: string }
export interface LastBackup  { at: string; byName: string; docCount: number }

const lockRef = (db: Firestore) => doc(db, 'maintenance', 'restore');
const lastRef = (db: Firestore) => doc(db, 'maintenance', 'lastBackup');

export const setRestoreLock   = (db: Firestore, lock: RestoreLock) => setDoc(lockRef(db), lock);
export const clearRestoreLock = (db: Firestore) => deleteDoc(lockRef(db));
export const watchRestoreLock = (db: Firestore, cb: (l: RestoreLock | null) => void) =>
  onSnapshot(lockRef(db), s => cb(s.exists() && (s.data() as RestoreLock).active ? s.data() as RestoreLock : null), () => cb(null));

export const saveLastBackup  = (db: Firestore, info: LastBackup) => setDoc(lastRef(db), info);
export const watchLastBackup = (db: Firestore, cb: (b: LastBackup | null) => void) =>
  onSnapshot(lastRef(db), s => cb(s.exists() ? s.data() as LastBackup : null), () => cb(null));
